import React from "react";
import DOMPurify from "dompurify";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import BackToTopButton from "@/components/BackToTopButton";
import PrayersAdmin from "@/components/PrayersAdmin";
import SelectionActionBar from "@/components/SelectionActionBar";
import { useSelection, useRowGestures } from "@/lib/useSelection";
import { useHighlightedFavourites } from "@/hooks/useHighlightedFavourites";
import { useLongPress } from "@/components/LongPressMenu";
import { colorIdToHex } from "@/lib/highlightColors";
import { stripDiacritics, highlightTextDiacritic } from "@/lib/textSearch";
import { CaretLeft, MagnifyingGlass } from "@phosphor-icons/react";

const PRAYERS_CHANGED = "soyapostol-prayers-changed";

// Local alias so the rest of the file reads naturally — same accent-
// insensitive highlighter used in Bible/Catechism's shared helper.
const highlightText = highlightTextDiacritic;

export default function Prayers() {
    const { lang, t } = useLang();
    const { user } = useAuth();
    const isAdmin = user && user.role === "admin";

    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [query, setQuery] = React.useState("");
    const [debouncedQuery, setDebouncedQuery] = React.useState("");
    const [searchResults, setSearchResults] = React.useState(null);  // null = not searching
    const [searching, setSearching] = React.useState(false);
    const [selected, setSelected] = React.useState(null);
    const [content, setContent] = React.useState(null);
    const [contentLoading, setContentLoading] = React.useState(false);
    const { selectedKeys, inSelectionMode, startOrReset, toggle, clear } = useSelection();

    // Lookup table for selection actions (slug → {title, content, category}).
    const allItems = React.useMemo(() => {
        const map = new Map();
        for (const cat of categories) {
            for (const it of (cat.items || [])) {
                map.set(it.slug, { ...it, category: cat.category });
            }
        }
        return map;
    }, [categories]);

    // Load the user's saved prayers so cards render with their pastel
    // backdrop and the SelectionActionBar can detect "is already colour X".
    // Saving a fresh prayer requires fetching the body content first, so
    // `buildPayload` does an on-demand fetch keyed by slug.
    const fetchPrayerContent = React.useCallback(async (slug) => {
        try {
            const r = await api.get(`/prayers/${slug}?lang=${lang}`);
            return r.data;
        } catch { return null; }
    }, [lang]);

    const { saved: savedPrayers, applyHighlight, clearHighlight, getRowColor } =
        useHighlightedFavourites({
            section: "prayers",
            filterFavorite: (f) => !!f.metadata?.slug,
            keyFromFavorite: (f) => f.metadata.slug,
            buildPayload: async (slug) => {
                const item = allItems.get(slug);
                if (!item) return null;
                const data = await fetchPrayerContent(slug);
                if (!data) return null;
                return {
                    title: data.title || item.title,
                    content: data.content || "",
                    source_url: data.source_url,
                    metadata: { slug, category: item.category },
                };
            },
            extraDeps: [allItems, fetchPrayerContent],
        });

    const refresh = React.useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/prayers?lang=${lang}`);
            setCategories(r.data.categories || []);
        } finally {
            setLoading(false);
        }
    }, [lang]);

    // Long-press handlers for the prayer detail view. Hook must be
    // called at the top level (React rules-of-hooks) — the handlers are
    // only attached to the <article> when we render the detail return.
    const detailSlug = selected ? (content?.slug || selected.slug) : null;
    const detailHandlers = useLongPress(() => {
        if (detailSlug) startOrReset(detailSlug);
    });

    React.useEffect(() => {
        setSelected(null); setContent(null);
        refresh();
    }, [refresh]);

    React.useEffect(() => {
        const handler = () => refresh();
        window.addEventListener(PRAYERS_CHANGED, handler);
        return () => window.removeEventListener(PRAYERS_CHANGED, handler);
    }, [refresh]);

    // Load the user's saved prayers so cards can render with their pastel
    // backdrop and the SelectionActionBar can detect "is already colour X".
    // Debounce the live query → debouncedQuery (250 ms).
    React.useEffect(() => {
        const id = setTimeout(() => setDebouncedQuery(query.trim()), 250);
        return () => clearTimeout(id);
    }, [query]);

    // Fetch full-text search whenever the debounced query is ≥ 2 chars.
    // Shorter strings reuse the title-only client filter to avoid the
    // 80-row server scan.
    React.useEffect(() => {
        if (debouncedQuery.length < 2) {
            setSearchResults(null);
            setSearching(false);
            return;
        }
        let cancelled = false;
        setSearching(true);
        (async () => {
            try {
                const r = await api.get(
                    `/prayers/search?lang=${lang}&q=${encodeURIComponent(debouncedQuery)}`,
                );
                if (!cancelled) setSearchResults(r.data.categories || []);
            } catch {
                if (!cancelled) setSearchResults([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        })();
        return () => { cancelled = true; };
    }, [debouncedQuery, lang]);

    const open = async (item) => {
        // Clicking a card while in selection mode toggles selection instead
        // of opening the detail view (matches Bible/CCC short-click).
        setSelected(item);
        setContent(null);
        setContentLoading(true);
        trackEvent("prayer_view", { slug: item.slug, title: item.title, lang });
        try {
            const res = await api.get(`/prayers/${item.slug}?lang=${lang}`);
            setContent(res.data);
        } finally { setContentLoading(false); }
    };

    // ---- Selection actions (long-press + colour highlight bar) --------
    const formatSelection = React.useCallback((keys) => {
        const lines = [];
        for (const slug of keys) {
            const m = allItems.get(slug);
            if (m) lines.push(m.title);
        }
        return lines.join("\n\n");
    }, [allItems]);

    const filteredCategories = React.useMemo(() => {
        const q = query.trim();
        // Server-side full-text results take priority once we have any
        // (debounced) query of length ≥ 2. They already include `snippet`
        // and `match_field` per item.
        if (q.length >= 2 && searchResults !== null) return searchResults;

        // For 1-char queries we fall back to the cheap title-only filter
        // over the cached catalogue — keeps typing snappy and avoids
        // sending 80-row scans for "a", "e", etc.
        if (q.length >= 1) {
            const needle = stripDiacritics(q.toLowerCase());
            return categories
                .map((c) => ({
                    ...c,
                    items: c.items.filter((i) =>
                        stripDiacritics((i.title || "").toLowerCase()).includes(needle),
                    ),
                }))
                .filter((c) => c.items.length > 0);
        }
        return categories;
    }, [categories, query, searchResults]);

    if (selected) {
        return (
            <div className="max-w-3xl mx-auto" data-testid="prayer-detail-page">
                <button onClick={() => { setSelected(null); setContent(null); }}
                    data-testid="prayer-back-btn"
                    className="inline-flex items-center gap-1.5 text-sm text-stoneMuted hover:text-sangre mb-8">
                    <CaretLeft size={14} weight="bold" /> {t("nav.prayers")}
                </button>
                {contentLoading && <p className="text-stoneMuted">{t("common.loading")}</p>}
                {content && (
                    <article
                        className="reading-prose select-none"
                        data-testid="prayer-detail-article"
                        data-selected={selectedKeys.has(detailSlug) ? "true" : "false"}
                        data-color={getRowColor(detailSlug) || ""}
                        style={{
                            WebkitUserSelect: "none",
                            WebkitTouchCallout: "none",
                            backgroundColor: colorIdToHex(getRowColor(detailSlug)) || undefined,
                            padding: getRowColor(detailSlug) ? "1rem" : undefined,
                            borderRadius: getRowColor(detailSlug) ? "0.5rem" : undefined,
                        }}
                        {...detailHandlers}
                    >
                        <div className="relative flex items-center justify-between border-b border-sand-300 pb-2 mb-6 gap-4">
                            <h1 className="heading-serif text-3xl sm:text-4xl tracking-tight leading-tight m-0">{content.title}</h1>
                        </div>
                        <PrayerBody content={content.content} />
                    </article>
                )}
                <BackToTopButton testId="prayer-detail-back-to-top" />
                <SelectionActionBar
                    selectedKeys={selectedKeys}
                    format={formatSelection}
                    applyHighlight={applyHighlight}
                    clearHighlight={clearHighlight}
                    getRowColor={getRowColor}
                    onClear={clear}
                    testIdBase="prayer-detail-selection"
                    copyLabelKey="prayers_actions.copy"
                    shareLabelKey="prayers_actions.share"
                    copySuccessKey="prayers_actions.copied"
                    shareTitleKey="prayers.share_title"
                />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto" data-testid="prayers-page">
            <p className="label-eyebrow mb-3">{t("nav.prayers")}</p>
            <h1 className="heading-serif text-4xl sm:text-5xl tracking-tight leading-none mb-3">{t("nav.prayers")}</h1>
            <p className="text-stoneMuted mb-2 max-w-2xl">{t("sections.prayers_desc")}</p>
            <p className="text-xs text-stoneFaint italic mb-10">{t("prayers_actions.long_press_hint")}</p>

            {isAdmin && (
                <PrayersAdmin apiCategories={categories} />
            )}

            <div
                className="sticky top-[56px] md:top-[72px] z-20 -mx-4 sm:-mx-6 lg:-mx-12 px-4 sm:px-6 lg:px-12 py-3 bg-sand-50/95 backdrop-blur-md border-b border-sand-300 mb-8"
                data-testid="prayers-search-wrap"
            >
                <div className="relative max-w-[720px] mx-auto">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneFaint" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("common.search")}
                        data-testid="prayers-search-input"
                        className="w-full pl-9 pr-3 py-2.5 bg-white border border-sand-300 rounded-md ui-sans text-sm focus:outline-none focus:border-sangre" />
                </div>
            </div>

            {loading && <p className="text-stoneMuted" data-testid="prayers-loading">{t("common.loading")}</p>}
            {!loading && categories.length === 0 && (
                <p className="text-stoneMuted" data-testid="prayers-empty">
                    {lang === "en" ? "English prayer catalog coming soon." : "No hay oraciones disponibles."}
                </p>
            )}

            {query.trim().length >= 2 && searching && filteredCategories.length === 0 && (
                <p className="text-stoneMuted text-sm mb-6" data-testid="prayers-search-loading">
                    {t("common.loading")}
                </p>
            )}
            {query.trim().length >= 1 && !searching && filteredCategories.length === 0 && (
                <p className="text-stoneMuted text-sm mb-6" data-testid="prayers-search-empty">
                    {lang === "en"
                        ? `No prayers match “${query.trim()}”.`
                        : `Ninguna oración coincide con “${query.trim()}”.`}
                </p>
            )}

            <div className="space-y-12" data-testid="prayers-categories">
                {filteredCategories.map((cat) => (
                    <section key={cat.category} data-testid={`cat-${cat.category}`}>
                        <h2 className="heading-serif text-2xl tracking-tight mb-5 border-b border-sand-300 pb-2">
                            {cat.category}
                        </h2>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {cat.items.map((item) => (
                                <PrayerCard
                                    key={item.slug}
                                    item={item}
                                    query={query}
                                    savedColor={savedPrayers.get(item.slug)?.color || null}
                                    isSaved={savedPrayers.has(item.slug)}
                                    isSelected={selectedKeys.has(item.slug)}
                                    inSelectionMode={inSelectionMode}
                                    onLongPress={() => startOrReset(item.slug)}
                                    onShortClick={() => toggle(item.slug)}
                                    onDoubleClick={clear}
                                    onOpen={() => open(item)}
                                />
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
            <BackToTopButton testId="prayers-back-to-top" />
            <SelectionActionBar
                selectedKeys={selectedKeys}
                format={formatSelection}
                applyHighlight={applyHighlight}
                clearHighlight={clearHighlight}
                getRowColor={getRowColor}
                onClear={clear}
                testIdBase="prayer-selection"
                copyLabelKey="prayers_actions.copy"
                shareLabelKey="prayers_actions.share"
                copySuccessKey="prayers_actions.copied"
                shareTitleKey="prayers.share_title"
            />
        </div>
    );
}

/* ================================================================== */
/* PrayerCard with long-press context menu                            */
/* ================================================================== */

function PrayerCard({
    item, onOpen, query, savedColor, isSaved,
    isSelected, inSelectionMode, onLongPress, onShortClick, onDoubleClick,
}) {
    // Long-press now enters multi-select mode (handled by the parent).
    // Short clicks toggle when already in selection mode; otherwise they
    // open the prayer detail like before.
    const gestureHandlers = useRowGestures({
        inSelectionMode,
        onLongPress,
        onShortClick,
        onDoubleClick,
    });
    const hasSnippet = !!item.snippet;
    const bg = colorIdToHex(savedColor);

    // Tap behaviour:
    //  • selection mode → toggle (matches Bible/CCC short-click).
    //  • normal mode    → open detail.
    const handleClick = (e) => {
        if (inSelectionMode) {
            e.stopPropagation();
            onShortClick();
            return;
        }
        onOpen?.(e);
    };
    // We use `useRowGestures` only for its long-press / dblclick handlers
    // here; the onClick is overridden so a fresh tap always opens detail
    // and a selection-mode tap always toggles (no debounce-induced races).
    const { onClick: _gestureClick, ...gestureRest } = gestureHandlers;

    return (
        <li className="relative">
            <button
                data-testid={`prayer-item-${item.slug}`}
                data-selected={isSelected ? "true" : "false"}
                data-color={savedColor || ""}
                className={[
                    "surface-card w-full text-left p-4 transition-colors select-none",
                    isSelected
                        ? "underline decoration-blue-600 decoration-2 underline-offset-4"
                        : "hover:border-sangre",
                    isSaved && !bg ? "border-l-2 border-sangre/50" : "",
                ].join(" ")}
                style={{
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                    backgroundColor: bg || undefined,
                }}
                {...gestureRest}
                onClick={handleClick}
            >
                <p className="reading-serif text-base leading-snug">
                    {highlightText(item.title, query)}
                </p>
                {hasSnippet && (
                    <p
                        className="ui-sans text-xs text-stoneMuted leading-relaxed mt-2 line-clamp-3"
                        data-testid={`prayer-item-${item.slug}-snippet`}
                    >
                        {highlightText(item.snippet, query)}
                    </p>
                )}
            </button>
        </li>
    );
}

/* ================================================================== */
/* PrayerDetailMenuButton was removed in favour of the unified         */
/* SelectionActionBar (long-press the article body to enter selection). */
/* ================================================================== */

// DOMPurify allow-list for prayer bodies. The admin editor (TipTap)
// produces a small set of inline tags + ordered/bullet lists; we keep
// the surface narrow so a corrupt entry can never inject scripts or
// dangerous attributes via dangerouslySetInnerHTML.
const PRAYER_DOMPURIFY_CFG = {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "u", "ul", "ol", "li", "span"],
    ALLOWED_ATTR: ["style"],
};

// Restrict the `style` attribute to a single safe declaration: `color`
// with a hex / rgb() / rgba() / named-colour value. Anything else is
// stripped so a malicious payload can't smuggle layout-breaking or
// position:fixed style rules into the page.
const SAFE_COLOR_RE = /^\s*color\s*:\s*(#[0-9a-f]{3,8}|rgba?\([^)]+\)|[a-z]+)\s*;?\s*$/i;
let prayerDomPurifyHookInstalled = false;
function ensurePrayerDomPurifyHook() {
    if (prayerDomPurifyHookInstalled) return;
    DOMPurify.addHook("uponSanitizeAttribute", (node, evt) => {
        if (evt.attrName !== "style") return;
        if (!SAFE_COLOR_RE.test(evt.attrValue)) {
            // Wipe disallowed style declarations entirely.
            evt.attrValue = "";
            evt.keepAttr = false;
        }
    });
    prayerDomPurifyHookInstalled = true;
}

function PrayerBody({ content }) {
    if (!content) return null;
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(content);
    if (!looksLikeHtml) {
        // Legacy plain-text path — keeps the existing rendering for any
        // prayer that was stored before the rich editor was introduced.
        return (
            <>
                {content.split(/\n+/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </>
        );
    }
    ensurePrayerDomPurifyHook();
    const safe = DOMPurify.sanitize(content, PRAYER_DOMPURIFY_CFG);
    return (
        <div
            data-testid="prayer-detail-html"
            dangerouslySetInnerHTML={{ __html: safe }}
        />
    );
}
